import React, { useState, useEffect } from 'react';
import { Heart, Play, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { musicService } from '../services/musicService';
import { authService } from '../services/authService';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const LikedSongs = ({ onPlaySong }) => {
  const navigate = useNavigate();
  const [likedSongs, setLikedSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!authService.isAuthenticated()) {
        setLoading(false);
        return;
      }

      try {
        const favorites = await musicService.getFavorites();
        setLikedSongs(favorites.songs || []);
      } catch (error) {
        console.error('Error loading liked songs:', error);
        // If auth error, clear storage and show message
        if (error.response?.status === 401 || error.response?.status === 520) {
          authService.logout();
          toast.info('Tu sesión expiró. Inicia sesión nuevamente para ver tus favoritos');
        } else {
          toast.error('Error al cargar favoritos');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#00CCFF] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Cargando favoritos...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!authService.isAuthenticated()) {
    return (
      <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="p-4 md:p-8">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/library')}
              className="flex items-center gap-2 text-gray-600 hover:text-[#00CCFF] mb-6 transition-colors duration-200"
            >
              <ArrowLeft size={20} />
              <span className="font-semibold">Volver</span>
            </button>

            <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center">
                <Heart size={48} className="text-white" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">Inicia sesión para ver tus favoritos</h3>
              <p className="text-gray-500 mb-6">Guarda tus canciones cristianas favoritas</p>
              <button
                onClick={() => navigate('/auth')}
                className="px-8 py-3 bg-gradient-to-r from-[#00CCFF] to-[#056A84] text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg"
              >
                Iniciar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="p-4 md:p-8">
        {/* Header */}
        <button
          onClick={() => navigate('/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#00CCFF] mb-6 transition-colors duration-200"
        >
          <ArrowLeft size={20} />
          <span className="font-semibold">Volver</span>
        </button>

        {/* Title Section */}
        <div className="mb-8 bg-gradient-to-r from-[#00CCFF] via-[#00CCFF]/90 to-[#056A84] rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Heart size={32} className="text-[#00CCFF]" fill="#00CCFF" />
            </div>
            <div>
              <h1 className="text-3xl md:text-5xl font-black text-white drop-shadow-lg">Canciones que te gustan</h1>
              <p className="text-white/90 font-medium mt-2">{likedSongs.length} canciones</p>
            </div>
          </div>
        </div>

        {/* Songs List */}
        {likedSongs.length > 0 ? (
          <div className="space-y-2">
            {likedSongs.map((song, index) => (
              <div
                key={song.id}
                onClick={() => onPlaySong(song, likedSongs)}
                className="flex items-center gap-4 p-4 rounded-xl hover:bg-white transition-all duration-300 cursor-pointer group shadow-sm hover:shadow-lg transform hover:-translate-y-1 bg-white/50"
              >
                <span className="text-gray-500 font-bold text-sm w-6">{index + 1}</span>
                <img
                  src={song.cover}
                  alt={song.title}
                  className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover shadow-md"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-gray-900 font-bold truncate">{song.title}</h3>
                  <p className="text-sm text-gray-500 truncate">{song.artist_name}</p>
                </div>
                <span className="text-gray-500 text-sm font-medium hidden md:inline">{song.duration}</span>
                <button className="opacity-0 group-hover:opacity-100 transition-all duration-300 w-12 h-12 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center hover:scale-110 shadow-lg">
                  <Play size={18} className="text-white ml-0.5" fill="white" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center">
              <Heart size={48} className="text-white" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">No tienes canciones favoritas aún</h3>
            <p className="text-gray-500 mb-6">Empieza a darle "me gusta" a tus canciones favoritas</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:scale-105 rounded-xl font-bold"
            >
              Explorar Música
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LikedSongs;
